import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, ShieldAlert, ShieldX } from "lucide-react";
import { cn } from "../lib/utils";

function labelMeta(label) {
  if (label === "Good")
    return {
      color: "hsl(var(--success))",
      icon: ShieldCheck,
      tagline: "Looks healthy. Always do your own research.",
    };
  if (label === "Warning")
    return {
      color: "hsl(var(--warning))",
      icon: ShieldAlert,
      tagline: "Mixed signals. Trade carefully.",
    };
  return {
    color: "hsl(var(--danger))",
    icon: ShieldX,
    tagline: "Multiple red flags. Treat as high risk.",
  };
}

export function SafetyScoreGauge({ score = 0, label = "Danger", topHoldersPct }) {
  const reduce = useReducedMotion();
  const meta = labelMeta(label);
  const Icon = meta.icon;
  const safe = Math.max(0, Math.min(100, score));
  const angle = (safe / 100) * 360;
  const ringStyle = {
    background: `conic-gradient(${meta.color} ${angle}deg, hsl(var(--border)) 0deg)`,
  };
  return (
    <div
      className="flex flex-col items-center justify-center text-center py-2"
      data-testid="safety-score-panel"
    >
      <motion.div
        className="relative h-44 w-44 sm:h-52 sm:w-52 rounded-full p-1.5"
        style={ringStyle}
        initial={reduce ? false : { scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        <div className="h-full w-full rounded-full bg-[hsl(var(--bg-elev-1))] border border-[hsl(var(--border))] flex flex-col items-center justify-center gap-1">
          <Icon size={22} style={{ color: meta.color }} />
          <div
            className="text-5xl sm:text-6xl font-semibold tabular-nums"
            data-testid="safety-score-value"
          >
            {safe}
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[hsl(var(--muted))]">
            / 100
          </div>
        </div>
      </motion.div>
      <div className="mt-4 flex items-center gap-2">
        <span
          className={cn(
            "px-2.5 py-1 text-xs font-semibold uppercase tracking-wider rounded-md border"
          )}
          style={{
            color: meta.color,
            borderColor: `${meta.color}66`,
            backgroundColor: `${meta.color}1f`,
          }}
          data-testid="safety-score-label"
        >
          {label}
        </span>
        {typeof topHoldersPct === "number" ? (
          <span className="text-xs text-[hsl(var(--muted))]">
            top holders <span className="font-mono text-[hsl(var(--fg))]">{topHoldersPct.toFixed(2)}%</span>
          </span>
        ) : null}
      </div>
      <p className="mt-2 max-w-sm text-xs text-[hsl(var(--muted))]">
        {meta.tagline}
      </p>
    </div>
  );
}
